import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import ServicePage from "@/pages/ServicePage";
import Pricing from "@/pages/Pricing";
import RequestInspection from "@/pages/RequestInspection";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import OpsDashboard from "@/pages/OpsDashboard";
import OpsInspect from "@/pages/OpsInspect";
import AppDownload from "@/pages/AppDownload";
import PortalLogin from "@/pages/PortalLogin";
import PortalHome from "@/pages/PortalHome";
import EvidenceShare from "@/pages/EvidenceShare";
import { BillingSuccess, BillingCancel } from "@/pages/BillingResult";
import { Loader2 } from "lucide-react";

const ProtectedRoute = ({ children, roles = ["admin"] }) => {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-900">
        <Loader2 className="h-6 w-6 animate-spin text-gold-500" />
      </div>
    );
  if (!user || !roles.includes(user.role)) return <Navigate to="/admin/login" replace />;
  return children;
};

const OwnerRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#051329]">
        <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  if (!user || user.role !== "owner") return <Navigate to="/portal/login" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services/:slug" element={<ServicePage />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/request-inspection" element={<RequestInspection />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/get-app" element={<AppDownload />} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/billing/cancel" element={<BillingCancel />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ops"
              element={
                <ProtectedRoute roles={["admin", "operator"]}>
                  <OpsDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ops/inspect/:visitId"
              element={
                <ProtectedRoute roles={["admin", "operator"]}>
                  <OpsInspect />
                </ProtectedRoute>
              }
            />
            <Route path="/evidence/:token" element={<EvidenceShare />} />
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route
              path="/portal"
              element={
                <OwnerRoute>
                  <PortalHome />
                </OwnerRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
